import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Inbox,
  Filter,
  Search,
  Flame,
  Clock,
  ExternalLink,
  RefreshCw,
  Loader2,
  Sparkles,
  Trash2,
  CalendarRange,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  subscribeLeads,
  subscribeCampaigns,
  deleteLead,
  categoryStyle,
  categoryLabel,
  LEAD_PLATFORMS,
  normalizePlatformKey,
  platformLabel,
} from "../../services/collavueStore";
import { syncCampaign } from "../../services/collavueApi";
import { useToast } from "../../contexts/ToastProvider";
import PageHeader from "../../components/collavue/PageHeader";
import Empty from "../../components/collavue/Empty";
import SyncAnimationOverlay from "../../components/collavue/SyncAnimationOverlay";
import ConfirmDialog from "../../components/collavue/ConfirmDialog";
import LeadChatPanel from "../../components/collavue/LeadChatPanel";
import { decodeHtmlEntities } from "../../utils/htmlEntities";
import {
  storageGet,
  storageSet,
  storageRemove,
} from "../../utils/browserSafety";

const STATUSES = [
  { v: "", label: "All" },
  { v: "unread", label: "Unread" },
  { v: "saved", label: "Saved" },
  { v: "contacted", label: "Contacted" },
  { v: "ignored", label: "Ignored" },
];

const CATEGORIES = [
  { v: "", label: "Any category" },
  { v: "hot", label: "Hot" },
  { v: "warm", label: "Warm" },
  { v: "research", label: "Research" },
  { v: "competitor_complaint", label: "Competitor complaint" },
];

const SORTS = [
  { v: "score", label: "Sort: Top score" },
  { v: "latest", label: "Sort: Latest" },
  { v: "custom", label: "Sort: Custom date range" },
];

// Source filter: leads carry the platform they were found on, so the inbox can
// be narrowed to one of them. "twitter" is normalized to "x" on read, so legacy
// leads still match the X option.
const PLATFORM_FILTERS = [
  { v: "", label: "All sources" },
  ...LEAD_PLATFORMS.map((p) => ({ v: p.v, label: p.label })),
];

const FILTER_STORAGE_KEY = "collavue.leads.campaignFilter";
const PLATFORM_FILTER_STORAGE_KEY = "collavue.leads.platformFilter";
const DAY_MS = 24 * 60 * 60 * 1000;

// Leads carry `postedAt` (epoch millis from the source platform) and fall back
// to the Firestore `createdAt` for anything scraped without a timestamp.
function leadTime(l) {
  if (Number.isFinite(l.postedAt)) return l.postedAt;
  if (l.createdAt?.toMillis) return l.createdAt.toMillis();
  return Number.isFinite(l.createdAt) ? l.createdAt : null;
}

// "YYYY-MM-DD" from a date input is parsed as UTC by `new Date`, which shifts
// the window by a day for anyone west of Greenwich. Build a local date instead.
function dayStart(value) {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).getTime();
}

function dayEnd(value) {
  const start = dayStart(value);
  return start === null ? null : start + DAY_MS - 1;
}

export default function LeadsInboxPage() {
  const { user } = useAuth();
  const { push } = useToast();
  const [params, setParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);

  // Persist the campaign filter so navigating to another page and back doesn't
  // wipe it. URL param wins; otherwise fall back to the stored value.
  const urlCampaignId = params.get("campaignId") || "";
  const storedCampaignId = storageGet(FILTER_STORAGE_KEY) || "";
  const campaignId = urlCampaignId || storedCampaignId;

  useEffect(() => {
    if (urlCampaignId && urlCampaignId !== storedCampaignId) {
      storageSet(FILTER_STORAGE_KEY, urlCampaignId);
    }
    // If URL has no campaignId but we have a stored one, hydrate the URL so
    // the rest of the page (and links sharing the URL) reflect the filter.
    if (!urlCampaignId && storedCampaignId) {
      const next = new URLSearchParams(params);
      next.set("campaignId", storedCampaignId);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCampaignId]);

  const [search, setSearch] = useState("");
  // Persist the source filter the same way the campaign filter is persisted,
  // so switching pages doesn't silently widen the inbox back to every source.
  const [platform, setPlatform] = useState(
    () => storageGet(PLATFORM_FILTER_STORAGE_KEY) || ""
  );
  const setPlatformFilter = (value) => {
    const next = PLATFORM_FILTERS.some((p) => p.v === value) ? value : "";
    setPlatform(next);
    if (next) storageSet(PLATFORM_FILTER_STORAGE_KEY, next);
    else storageRemove(PLATFORM_FILTER_STORAGE_KEY);
  };
  const [minScore, setMinScore] = useState(0);
  const [maxScore, setMaxScore] = useState(100);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("score");
  // AI filter set by the lead assistant ("find every lead that mentions…").
  // Restricts the inbox to the returned lead IDs until cleared.
  const [aiFilter, setAiFilter] = useState(null); // { ids: Set, label }

  const [syncing, setSyncing] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.id === campaignId) || null,
    [campaigns, campaignId]
  );

  const onSync = async () => {
    const target =
      activeCampaign || (campaigns.length === 1 ? campaigns[0] : null);
    if (!target) {
      push({
        title: "Pick a campaign",
        desc: "Select a single campaign filter to run a sync.",
        variant: "error",
      });
      return;
    }
    setSyncing(true);
    setSyncOpen(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const r = await syncCampaign(target.id);
      setSyncResult(r);
      // Pin the filter to this campaign so the post-sync inbox shows the
      // freshly-synced leads only — saves the user from re-applying.
      setCampaignFilter(target.id);
      push({
        title: "Sync complete",
        desc: `${r.stats?.leadsCreated || 0} new leads, ${
          r.stats?.hotLeads || 0
        } hot.`,
        variant: "success",
      });
    } catch (e) {
      setSyncError(e.message);
      push({ title: "Sync failed", desc: e.message, variant: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const closeOverlay = () => {
    setSyncOpen(false);
    setSyncResult(null);
    setSyncError(null);
  };

  const askDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteLead(deleteId);
      push({ title: "Lead deleted", variant: "success" });
      setDeleteId(null);
    } catch (err) {
      push({ title: "Delete failed", desc: err.message, variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const a = subscribeLeads(campaignId ? { campaignId } : null, setLeads);
    const b = subscribeCampaigns((items) => {
      setCampaigns(items);
      setCampaignsLoaded(true);
    });
    return () => {
      a();
      b();
    };
  }, [user, campaignId]);

  // A filter pointing at a deleted campaign silently empties the inbox and
  // sticks around forever (it's persisted in localStorage). Once campaigns
  // have loaded, drop any filter that no longer matches a real campaign.
  useEffect(() => {
    if (!campaignsLoaded || !campaignId) return;
    if (!campaigns.some((c) => c.id === campaignId)) {
      setCampaignFilter("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignsLoaded, campaignId, campaigns]);

  // The date window only applies in the custom-range sort, so switching sorts
  // never silently keeps hiding leads.
  const from = sort === "custom" ? dayStart(dateFrom) : null;
  const to = sort === "custom" ? dayEnd(dateTo) : null;

  const filtered = useMemo(() => {
    const list = leads
      .filter((l) => !aiFilter || aiFilter.ids.has(l.id))
      // Treat a missing score as 0 so unscored leads still show at min 0
      // (undefined >= 0 is false, which used to hide them entirely).
      .filter((l) => {
        const score = Number(l.intentScore || 0);
        return score >= minScore && score <= maxScore;
      })
      .filter((l) => {
        if (from === null && to === null) return true;
        const t = leadTime(l);
        // An undated lead can't be placed in the window, so leave it out
        // rather than pretend it belongs there.
        if (t === null) return false;
        return (from === null || t >= from) && (to === null || t <= to);
      })
      .filter((l) => !status || l.status === status)
      .filter((l) => !category || l.category === category)
      .filter((l) => !platform || normalizePlatformKey(l.platform) === platform)
      .filter((l) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          decodeHtmlEntities(l.title).toLowerCase().includes(q) ||
          decodeHtmlEntities(l.body).toLowerCase().includes(q) ||
          (l.community || "").toLowerCase().includes(q)
        );
      });
    if (sort === "custom") {
      return list.sort((a, b) => (leadTime(b) || 0) - (leadTime(a) || 0));
    }
    if (sort === "latest") {
      return list.sort((a, b) => {
        const ta = a.createdAt?.toMillis
          ? a.createdAt.toMillis()
          : a.createdAt || 0;
        const tb = b.createdAt?.toMillis
          ? b.createdAt.toMillis()
          : b.createdAt || 0;
        return tb - ta;
      });
    }
    return list.sort((a, b) => (b.intentScore || 0) - (a.intentScore || 0));
  }, [
    leads,
    minScore,
    maxScore,
    from,
    to,
    status,
    category,
    platform,
    search,
    sort,
    aiFilter,
  ]);

  const sortLabel =
    sort === "latest"
      ? "newest first"
      : sort === "custom"
      ? "post date"
      : "intent score";

  const setCampaignFilter = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("campaignId", id);
    else next.delete("campaignId");
    setParams(next);
    if (id) storageSet(FILTER_STORAGE_KEY, id);
    else storageRemove(FILTER_STORAGE_KEY);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 bg-[#fafafa] min-h-screen font-sans text-[#0B1038]">
      <SyncAnimationOverlay
        open={syncOpen}
        campaign={
          activeCampaign || (campaigns.length === 1 ? campaigns[0] : null)
        }
        result={syncResult}
        error={syncError}
        onClose={closeOverlay}
      />
      <ConfirmDialog
        open={!!deleteId}
        title="Delete this lead?"
        body="It will be removed from your inbox permanently. This can't be undone."
        confirmLabel="Delete lead"
        variant="danger"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => (deleting ? null : setDeleteId(null))}
      />
      <PageHeader
        title="Lead inbox"
        subtitle={`${filtered.length} matching leads${
          platform ? ` from ${platformLabel(platform)}` : ""
        } — sorted by ${sortLabel}.`}
        actions={
          <button
            onClick={onSync}
            disabled={syncing || campaigns.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0B1038] text-white text-sm font-semibold hover:bg-[#151c54] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {activeCampaign
              ? `Sync ${activeCampaign.name}`
              : campaigns.length === 1
              ? "Sync now"
              : "Pick a campaign to sync"}
          </button>
        }
      />

      <div className="bg-white border border-gray-200 rounded-md p-4 sm:p-5 mb-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, body, author…"
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-shadow"
            />
          </div>
          <select
            value={campaignId}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
          >
            <option value="">All campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
          >
            {STATUSES.map((s) => (
              <option key={s.v} value={s.v}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
          <select
            value={platform}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
            aria-label="Filter leads by source"
          >
            {PLATFORM_FILTERS.map((s) => (
              <option key={s.v || "all"} value={s.v}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
          >
            {CATEGORIES.map((s) => (
              <option key={s.v} value={s.v}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 sm:py-2 border border-gray-300 rounded-sm text-sm outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 bg-white"
            aria-label="Sort leads"
          >
            {SORTS.map((s) => (
              <option key={s.v} value={s.v}>
                {s.label}
              </option>
            ))}
          </select>
          <div className="w-full sm:w-64">
            <div className="flex items-center justify-between gap-2 text-sm text-gray-600">
              <span className="inline-flex items-center gap-2">
                <Filter className="w-4 h-4 text-[#0B1038]" />
                Score range
              </span>
              <span className="font-semibold text-[#0B1038] tabular-nums">
                {minScore} – {maxScore}
              </span>
            </div>
            <div className="range-dual relative h-6 mt-1.5">
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-gray-200" />
              <div
                className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-blue-600"
                style={{ left: `${minScore}%`, right: `${100 - maxScore}%` }}
              />
              <input
                type="range"
                min={0}
                max={100}
                value={minScore}
                // Dragging one thumb past the other pushes it along rather
                // than wedging both at the same value with no way out.
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setMinScore(v);
                  if (v > maxScore) setMaxScore(v);
                }}
                // When the thumbs sit on top of each other, whichever has
                // room to move stays on top so the pair can be pulled apart.
                style={{ zIndex: minScore > 100 - maxScore ? 5 : 3 }}
                aria-label="Minimum intent score"
              />
              <input
                type="range"
                min={0}
                max={100}
                value={maxScore}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setMaxScore(v);
                  if (v < minScore) setMinScore(v);
                }}
                style={{ zIndex: 4 }}
                aria-label="Maximum intent score"
              />
            </div>
          </div>
          {aiFilter && (
            <span className="inline-flex self-start items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-700">
              <Sparkles className="w-3 h-3" />
              AI: {aiFilter.label} ({filtered.length})
              <button
                onClick={() => setAiFilter(null)}
                aria-label="Clear AI filter"
                className="hover:text-rose-600"
              >
                ×
              </button>
            </span>
          )}
          {platform && (
            <button
              onClick={() => setPlatformFilter("")}
              className="self-start text-xs font-semibold text-blue-600 hover:underline"
            >
              Clear source filter
            </button>
          )}
          {campaignId && (
            <button
              onClick={() => setCampaignFilter("")}
              className="self-start sm:ml-auto text-xs font-semibold text-blue-600 hover:underline"
            >
              Clear campaign filter
            </button>
          )}
        </div>

        {sort === "custom" && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1038]">
                <CalendarRange className="w-4 h-4 text-blue-600" />
                Custom date range
              </span>
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                  }}
                  className="text-xs font-semibold text-blue-600 hover:underline"
                >
                  Clear dates
                </button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:max-w-lg">
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  From
                </span>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full min-h-[42px] px-3 py-2.5 border border-gray-300 rounded-sm text-sm bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  To
                </span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full min-h-[42px] px-3 py-2.5 border border-gray-300 rounded-sm text-sm bg-white outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Leave a field empty for an open-ended range. Leads without a post
              date are hidden while a range is set.
            </p>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <Empty
          icon={Inbox}
          title="No leads match"
          body="Try widening the score range or date range, clearing filters, or running a campaign sync."
          cta={
            <Link
              to="/campaigns"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 mt-2 rounded-sm bg-[#0B1038] hover:bg-[#151c54] text-white font-semibold transition-colors"
            >
              Go to campaigns
            </Link>
          }
        />
      ) : (
        <ul className="space-y-3">
          {filtered.map((l) => (
            <li
              key={l.id}
              className="bg-white border border-gray-200 rounded-md p-5 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <Link to={`/leads/${l.id}`} className="block">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-block text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-sm border ${categoryStyle(
                          l.category
                        )}`}
                      >
                        {categoryLabel(l.category)}
                      </span>
                      {l.status === "unread" && (
                        <span className="text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200">
                          New
                        </span>
                      )}
                      <span className="text-xs text-gray-500 font-medium">
                        {platformLabel(l.platform)} · {l.community}
                      </span>
                    </div>
                    <div className="mt-2 font-display text-xl text-[#0B1038] truncate group-hover:text-blue-600 transition-colors">
                      {decodeHtmlEntities(l.title) || decodeHtmlEntities(l.body)?.slice(0, 100)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                      {decodeHtmlEntities(l.body)}
                    </div>
                    {l.matchReason && (
                      <div className="text-xs text-gray-500 mt-3 border-l-2 border-gray-200 pl-2">
                        <span className="font-semibold text-[#0B1038]">
                          Match context:
                        </span>{" "}
                        {decodeHtmlEntities(l.matchReason)}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end">
                    <div className="inline-flex items-center gap-1.5 text-2xl font-display text-[#0B1038]">
                      <Flame className="w-5 h-5 text-blue-600" />
                      {l.intentScore}
                    </div>
                    <div className="text-xs text-gray-500 font-medium mt-1">
                      Risk: {l.riskScore || 0}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-2 inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {l.postedAt
                        ? new Date(l.postedAt).toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                </div>
              </Link>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
                {l.url ? (
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-[#0B1038] transition-colors"
                  >
                    View source <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={(e) => askDelete(e, l.id)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-rose-200 bg-white text-rose-700 text-xs font-semibold hover:bg-rose-50 transition-colors"
                  aria-label="Delete lead"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <LeadChatPanel
        campaignId={campaignId}
        campaignName={activeCampaign?.name}
        activeFilterLabel={aiFilter?.label || ""}
        onApplyFilter={(ids, label) =>
          setAiFilter({ ids: new Set(ids), label: label || "AI search" })
        }
        onClearFilter={() => setAiFilter(null)}
      />
    </div>
  );
}
